// src/hooks/use-table-columns.js
import { h } from 'vue';
import FhFieldShow from '@/components/fh-set-fields/field-list-main/index.vue';
import { tools } from '@/utils/tools';
import { useUserStore } from '@/store';
/******************** 迁移工具 start ***********************/
import { useEnvConfig } from '@/hooks/env-config';
import MigrationDetail from '@/components/migration-detail/migration-detail.vue';
import fieldPermission from '@/hooks/field-permission';
/******************** 迁移工具 end ***********************/

const { isRequiredField, updateFieldPermission } = fieldPermission();
/**
 * 表格列配置相关方法
 */
export default function useTableColumns(onFieldUpdate) {
  // 添加对齐方式逻辑判断
  const ALIGN_RIGHT_TYPES = new Set([7, 16, 23]); // 使用Set提高查找效率
  const getColumnAlign = (fieldType, fieldExt) => {
    if (ALIGN_RIGHT_TYPES.has(fieldType)) return 'right';
    if (fieldType !== 24) return 'left';
    if (fieldType !== 24) return 'left';
    // 只有当类型是24时才尝试解析JSON
    if (typeof fieldExt !== 'string') return 'left';
    try {
      const { type } = JSON.parse(fieldExt) || {};
      return type === 0 ? 'left' : 'right';
    } catch {
      return 'left';
    }
  };

  // 缓存字符串长度计算结果
  const strLenCache = new Map();
  const MAX_CACHE_SIZE = 100;

  // 使用更严格的缓存策略，避免内存泄漏
  const getStrLen = (str) => {
    if (!str) return 0;
    if (strLenCache.has(str)) {
      return strLenCache.get(str);
    }
    const len = tools.getStrLen(str);
    strLenCache.set(str, len);
    if (strLenCache.size > MAX_CACHE_SIZE) {
      // 删除最旧的50项
      const keys = Array.from(strLenCache.keys()).slice(0, 50);
      keys.forEach((key) => strLenCache.delete(key));
    }
    return len;
  };

  // 获取子表单字段
  const getSubFormField = (fieldVos) => {
    return fieldVos?.map((v) => ({
      fieldKey: v.fieldKey,
      entityId: v.entityId,
      id: v.id,
      fieldType: v.fieldType
    }));
  };

  /**
   * 创建子表单列配置
   * @param {Object} item 字段配置
   * @param {Object} options 依赖选项
   * @param {Ref} options.tableData 表格数据引用
   * @param {Ref} options.entity 当前实体引用
   */
  const createChildColumns = (item, { tableData, entity, tableLevel, callbackList }) => {
    const colTypeChildren = [];
    item?.fieldVos?.forEach((el) => {
      const defaultCellWidth = [8, 9].includes(el.fieldType) ? 160 : getStrLen(el.fieldName) <= 6 ? 120 : getStrLen(el.fieldName) > 10 ? 220 : getStrLen(el.fieldName) * 20;
      const childRender = (row) => {
        const record = row;
        const subFormData = record?.[item?.fieldKey] || [];
        if (subFormData?.length > 0) {
          return h('div', { class: 'child-table-container' }, [
            ...subFormData.map((subFormRecord) => {
              const params = {
                col: { ...el, setting: { ...el } },
                row: subFormRecord,
                fieldKey: el.fieldKey,
                subFieldFullKey: `${item?.fieldKey}.${el?.fieldKey}`,
                mainField: el.mainField,
                isRequired: isRequiredField(`${item?.fieldKey}.${el?.fieldKey}`, row?.PERMISSION, el.isRequired),
                fieldValue: subFormRecord?.[el.fieldKey],
                fieldType: el.fieldType,
                fieldExt: el.fieldExt,
                entity: entity,
                subEntityId: item.fieldExt,
                isSubForm: true,
                sourceType: 1,
                isAutoHeight: false,
                tableLevel,
                permissions: subFormRecord.LOGIC_ROLE_PERMISSION,
                PERMISSION: row?.PERMISSION
              };
              return h(FhFieldShow, {
                params,
                onCallbackList: (callback) => {
                  if (callback?.businessId) {
                    //替换成主数据id获取权限规则
                    callback.subFormFieldKey = item?.fieldKey;
                    callback.subId = callback.businessId;
                    callback.businessId = row.id;
                    updateFieldPermission(tableData.value, entity, callback);
                    callbackList?.(callback); // 调用组件传递的回调函数
                  }
                }
              });
            })
          ]);
        } else {
          return h('span', { class: 'table-cell-render-empty' }, '-');
        }
      };
      const getColType = {
        colKey: `${item.fieldKey}.${el.fieldKey}`,
        title: el.fieldName,
        fieldName: el.fieldName,
        fieldType: el.fieldType,
        fieldExt: el.fieldExt,
        width: defaultCellWidth,
        resize: { minWidth: [8, 9].includes(el.fieldType) ? 160 : getStrLen(el.fieldName) <= 5 ? 100 : 150, maxWidth: 500 },
        iconMore: true,
        isSubForm: true,
        align: getColumnAlign(el.fieldType, el.fieldExt),
        setting: item?.fieldVos?.length ? getSubFormField(item.fieldVos) : null,
        render: childRender
      };
      colTypeChildren.push(getColType);
    });
    return colTypeChildren;
  };

  /**
   * 创建列配置
   * @param {Object} item 字段配置
   * @param {Object} options 依赖选项
   * @param {Ref} options.tableData 表格数据引用
   * @param {Ref} options.entity 当前实体引用
   */
  const createColumnConfig = (item, { tableData, entity, tableLevel, callbackList, virtual }) => {
    const k = item.fieldKey;
    const frozen = item.frozen;
    const oldFieldType = item.fieldType;
    const fieldType = item.fieldType == 21 ? item?.business?.fieldType ?? 21 : item?.fieldType;
    const fieldExt = item.fieldType == 21 ? item?.business?.fieldExt : item?.fieldExt;
    /******************** 迁移工具 start ***********************/
    const { userInfo } = useUserStore();
    const { fieldMappings, config } = useEnvConfig(entity);
    /******************** 迁移工具 end ***********************/

    const defaultWidth = [8, 9].includes(fieldType) ? 160 : getStrLen(item.fieldName) <= 6 ? 120 : getStrLen(item.fieldName) > 10 ? 220 : getStrLen(item.fieldName) * 20;
    return {
      colKey: k,
      title: item.fieldName,
      fieldName: item.fieldName,
      fieldType: fieldType,
      oldFieldType: oldFieldType,
      fieldExt: fieldExt,
      fieldSort: item.fieldSort,
      width: item.mainField || [12, 25].includes(fieldType) ? 250 : defaultWidth,
      resize: { minWidth: [8, 9].includes(fieldType) ? 160 : getStrLen(item.fieldName) <= 5 ? 100 : 150, maxWidth: 500 },
      fixed: frozen ? 'left' : undefined,
      align: getColumnAlign(fieldType, fieldExt),
      iconMore: true,
      colType: item.type === 1 ? '基础字段' : '其他',
      isSubForm: false,
      aggregationType: item.aggregationType,
      virtual: virtual, // 虚拟列表
      render: (row) => {
        const params = {
          col: { ...item },
          row,
          align: getColumnAlign(fieldType, fieldExt),
          fieldKey: k,
          mainField: item.mainField,
          isRequired: isRequiredField(item?.fieldKey, row?.PERMISSION, item.isRequired),
          fieldValue: row?.[k],
          fieldType: fieldType,
          fieldExt: fieldExt,
          entity: entity,
          sourceType: 1, //1:列表 2：详情 3：关联对象表格
          isSubForm: false,
          isAutoHeight: true,
          tableLevel,
          permissions: row?.LOGIC_ROLE_PERMISSION,
          PERMISSION: row?.PERMISSION
        };

        /******************** 迁移工具 start ***********************/
        // TAG: 迁移工具
        if (userInfo.orgId == config.migrationOrgId && [config.migrationToolsEntity, config.migrationToolsV2Entity].includes(entity) && k == fieldMappings.detailOperation) {
          return h(MigrationDetail, {
            params
          });
        }
        /******************** 迁移工具 end ***********************/
        return h(FhFieldShow, {
          params,
          onCallbackList: (callback) => {
            if (callback?.businessId) {
              updateFieldPermission(tableData.value, entity, callback);
              onFieldUpdate?.(callback);
              callbackList?.(callback); // 调用组件传递的回调函数
            }
          }
        });
      }
    };
  };
  // 添加额外字段，处理子表单数据结构
  // 定义一个函数，用于提取条件中的字段
  const extractFields = (conditions) => {
    // 定义一个空数组，用于存储提取的字段
    const fields = [];
    // 定义一个函数，用于遍历条件
    function traverseConditions(conditions) {
      // 遍历条件
      for (const condition of conditions) {
        // 如果条件有子条件，则递归遍历子条件
        if (condition.children && condition.children.length) {
          traverseConditions(condition.children);
          // 如果条件有字段，则将字段添加到数组中
        } else if (condition.field) {
          // 如果字段的备注是JSON格式，则提取JSON中的字段
          if (tools.isJSON(condition.remark)) {
            const subFormField = `${condition.field}.${JSON.parse(condition.remark)?.fieldKey}`;
            fields.push(subFormField);
            // 否则，直接将字段添加到数组中
          } else {
            fields.push(condition.field);
          }
        }
      }
    }
    // 调用遍历函数
    traverseConditions(conditions);
    // 返回提取的字段数组
    return fields;
  };
  return {
    createChildColumns,
    createColumnConfig,
    extractFields
  };
}
